import React, { useEffect } from 'react';
import { Avatar, Card, Col, Row, Button, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { debounce, startCase } from 'lodash';

export const TaskRecentActivity = () => {
  const {
    fetchHistories,
    histories = [],
    activity,
    currentUser,
    setActivityPagination,
    activityPagination,
    fetchTaskByIds,
    taskByIdsLoading,
    showMyTasks,
    setActivity,
  } = useTaskContext();

  useEffect(() => {
    fetchHistories({
      variables: {
        where: {
          type: 'TASK',
        },
        orderBy: 'createdAt_DESC',
        first: 80,
      },
    });
  }, [fetchHistories]);

  const getActivityDetails = (history) => {
    switch (history.action) {
      case 'CREATED':
        return { action: 'Task Created', icon: '🆕' };
      case 'ADDED':
        return { action: 'Comment Added', icon: '💬' };
      case 'UPDATED':
        const fieldName = (history.data && history.data.fieldName.split('.')[0]) || null;
        switch (fieldName) {
          case 'assigneeUsers':
            return { action: 'Task Assigned', icon: '👤' };
          case 'status':
            return history.data.newValue === 'COMPLETED'
              ? { action: 'Task Completed', icon: '✅' }
              : { action: 'Status Changed', icon: '🔄' };
          case 'urgency':
            return { action: 'Task Urgency Changed', icon: '⚡' };
          case 'taskDetails':
            return { action: 'Task Details Updated', icon: '📝' };
          case 'dueDate':
            return { action: 'Task Deadline Updated', icon: '⏰' };
          default:
            return { action: 'Task Updated', icon: '🔄' };
        }
      default:
        return { action: 'Activity', icon: '🔹' };
    }
  };

  const getTaskName = (referenceId) => {
    const foundTask = activity.find((t) => t.id === referenceId);
    return foundTask ? foundTask.name : `${referenceId}`;
  };

  const allowedTaskIds = activity.map((task) => task.id);
  const filteredHistories = histories.filter((history) =>
    allowedTaskIds.includes(history.referenceId)
  );

  const activities = filteredHistories
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((history) => {
      const { action, icon } = getActivityDetails(history);
      return {
        action,
        task: (
          <Link to={`/tasks/details/${history.referenceId}`}>
            {getTaskName(history.referenceId)}
          </Link>
        ),
        user: (history.author && history.author.name) || 'Unknown',
        time: moment(history.createdAt).fromNow(),
        icon,
      };
    });

  const maxItems = 2 * activityPagination.pageSize;
  const limitedActivities = activities.slice(0, maxItems);
  const totalPages = Math.ceil(limitedActivities.length / activityPagination.pageSize);
  const isEmpty = totalPages === 0;
  const currentPage = isEmpty ? 0 : activityPagination.current;

  const visibleActivities = limitedActivities.slice(
    (activityPagination.current - 1) * activityPagination.pageSize,
    activityPagination.current * activityPagination.pageSize
  );

  const handlePaginationChange = (page, pageSize) => {
    setActivityPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const handleDotClick = (pageIndex) => {
    setActivityPagination((prev) => ({
      ...prev,
      current: pageIndex + 1,
    }));
  };

  useEffect(() => {
    const referenceIds = histories.map((h) => h.referenceId);
    const idArray = referenceIds.length > 0 ? referenceIds : [];

    if (idArray.length > 0 && !(showMyTasks && currentUser.isPreviewOrg)) {
      const handleFetchTasksPaginated = debounce(() => {
        fetchTaskByIds({
          variables: {
            where: {
              AND: [
                { id_in: idArray },
                {
                  OR: [
                    {
                      AND: [
                        ...(showMyTasks && !currentUser.isPreviewOrg
                          ? [
                              {
                                assigneeUsers_some: { id: currentUser.id },
                              },
                            ]
                          : []),
                        {
                          createdBy: {
                            roles_some: {
                              org: {
                                OR: [
                                  {
                                    parentOrg_some: {
                                      id: currentUser.role.org.id,
                                    },
                                  },
                                  { id: currentUser.role.org.id },
                                ],
                              },
                            },
                          },
                        },
                      ],
                    },
                    ...(showMyTasks
                      ? [
                          { createdBy: { id: currentUser.id } },
                          {
                            followers_some: { id: currentUser.id },
                          },
                          {
                            collaborators_some: { id: currentUser.id },
                          },
                        ]
                      : []),
                  ],
                },
              ],
            },
            orderBy: 'updatedAt_DESC',
            first: 80,
            skip: 0,
          },
        });
      }, 500);

      handleFetchTasksPaginated();
    } else if (showMyTasks && currentUser.isPreviewOrg) {
      setActivity([]);
    }
  }, [
    histories,
    fetchTaskByIds,
    currentUser.role.org.id,
    currentUser.id,
    showMyTasks,
    currentUser.isPreviewOrg,
    setActivity,
  ]);

  return (
    <Card
      title={
        <span style={{ fontSize: '20px' }}>
          {showMyTasks ? 'My Recent Activity' : 'All Recent Activity'}
        </span>
      }
      style={{
        marginBottom: '20px',
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
      }}
    >
      {/* Pagination controls */}
      <Row style={{ marginBottom: '15px', justifyContent: 'space-between' }}>
        <Button
          icon={<LeftOutlined />}
          onClick={() =>
            handlePaginationChange(
              activityPagination.current === 1 ? totalPages : activityPagination.current - 1,
              activityPagination.pageSize
            )
          }
          disabled={isEmpty}
          style={{ marginRight: '10px' }}
          size="medium"
          shape="circle"
        />
        <span style={{ margin: '0 10px', fontSize: '15px', color: '#666' }}>
          {isEmpty ? '' : `Page ${currentPage} of ${totalPages}`}
        </span>
        <Button
          icon={<RightOutlined />}
          onClick={() =>
            handlePaginationChange(
              activityPagination.current >= totalPages ? 1 : activityPagination.current + 1,
              activityPagination.pageSize
            )
          }
          disabled={isEmpty}
          style={{ marginLeft: '10px' }}
          size="medium"
          shape="circle"
        />
      </Row>

      {taskByIdsLoading && <Spin style={{ display: 'flex', justifyContent: 'center' }} />}
      {!taskByIdsLoading && visibleActivities.length === 0 ? (
        <Row justify="center" style={{ padding: '5px 0', fontSize: '16px', color: '#666' }}>
          No Recent Activity
        </Row>
      ) : (
        <Row gutter={[16, 16]} justify="center">
          {!taskByIdsLoading &&
            visibleActivities.map((activity, index) => (
              <Col key={index} xs={24} sm={12} md={6} lg={6}>
                <Card
                  style={{
                    borderRadius: '10px',
                    height: '100%',
                    borderBottom: '3px solid #f1f5f9',
                  }}
                >
                  <Row
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Row
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <Avatar
                        style={{
                          width: '35px',
                          height: '35px',
                          backgroundColor: '#dbeafe',
                          fontSize: '30px',
                        }}
                      >
                        {activity.icon}
                      </Avatar>
                      <Col style={{ fontWeight: '500' }}>{activity.action}</Col>
                    </Row>
                    <Col
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Row
                        style={{
                          fontWeight: 'bold',
                          marginTop: '12px',
                          wordBreak: 'break-word',
                        }}
                      >
                        {activity.task}
                      </Row>
                      <Row style={{ gap: '5px' }}>
                        {startCase(activity.user)}
                        <span style={{ color: '#666' }}> • {activity.time}</span>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              </Col>
            ))}
        </Row>
      )}

      {/* Pagination Dots */}
      <Row justify="center" style={{ marginTop: '15px' }}>
        {!taskByIdsLoading &&
          Array.from({ length: totalPages }).map((_, index) => (
            <div
              key={index}
              onClick={() => handleDotClick(index)}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: activityPagination.current === index + 1 ? '#1890ff' : '#d9d9d9',
                margin: '0 4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
      </Row>
    </Card>
  );
};

export default TaskRecentActivity;
